"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { getLearningArticle } from "@/app/learning/learningArticles";
import { getGlossaryTerm } from "@/lib/glossary";
import { buildBreadcrumbJsonLd } from "@/lib/seo";
import { strategyCatalogBySlug } from "@/lib/strategyCatalog";

const SEGMENT_LABELS: Record<string, string> = {
  about: "About",
  calculator: "Calculator",
  contact: "Contact",
  "daily-tracker": "Daily Tracker",
  data: "Pattern Data",
  divergences: "Divergences",
  flows: "Flows",
  funding: "Funding",
  glossary: "PSX Glossary",
  indices: "Indices",
  learning: "Learning",
  market: "Market",
  "market-overview": "Market Overview",
  "market-stats": "Market Stats",
  "money-flow": "Money Flow",
  news: "News",
  "off-market-analysis": "Off-Market Analysis",
  portfolio: "Portfolio",
  "pressure-index": "Pressure Index",
  "privacy-policy": "Privacy Policy",
  "relative-strength": "Relative Strength",
  scanner: "Scanner",
  strategies: "Strategies",
  stock: "Stocks",
  "trade-journal": "Trade Journal",
};

function humanizeSegment(segment: string) {
  return segment
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSegmentLabel(segment: string, allSegments: string[], index: number) {
  const topLevel = allSegments[0];

  if (topLevel === "strategies" && index === 1) {
    return strategyCatalogBySlug[segment]?.name ?? humanizeSegment(segment);
  }

  if (topLevel === "learning" && index === 1) {
    return getLearningArticle(segment)?.title ?? humanizeSegment(segment);
  }

  if (topLevel === "glossary" && index === 1) {
    return getGlossaryTerm(segment)?.term ?? humanizeSegment(segment);
  }

  if (topLevel === "stock" && index === 1) {
    return segment.toUpperCase();
  }

  return SEGMENT_LABELS[segment] ?? humanizeSegment(segment);
}

export default function SeoBreadcrumbs() {
  const pathname = usePathname();

  if (!pathname || pathname === "/" || pathname.startsWith("/admin") || pathname.startsWith("/login")) {
    return null;
  }

  const segments = pathname.split("/").filter(Boolean);
  const items = [{ name: "Home", path: "/" }];
  let currentPath = "";

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    items.push({
      name: getSegmentLabel(segment, segments, index),
      path: currentPath,
    });
  });

  const jsonLd = buildBreadcrumbJsonLd(items);

  return (
    <div className="seoBreadcrumbBand">
      <div className="page-header-container seoBreadcrumbContainer">
        <nav aria-label="Breadcrumb" className="seoBreadcrumbs">
          <ol className="seoBreadcrumbList">
            {items.map((item, index) => {
              const isCurrentPage = index === items.length - 1;
              return (
                <li key={item.path} className="seoBreadcrumbItem">
                  {isCurrentPage ? (
                    <span aria-current="page">{item.name}</span>
                  ) : (
                    <Link href={item.path}>{item.name}</Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </div>
  );
}
